import qs from 'qs';
import forEach from 'lodash/forEach';
import focusGuard from './focus-guard';
import { validations } from './utils';

function clearErrors() {
    const errors = [...document.querySelectorAll('.stepped-form__error')];
    const invalids = [...document.querySelectorAll('.is-invalid')];
    errors.forEach(error => error.parentNode.removeChild(error));
    invalids.forEach(invalid => invalid.classList.remove('is-invalid'));
}

async function createUser(formData) {
    return fetch('/calculator/user', {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'Content-Type': 'application/x-www-form-urlencoded',
        },
        credentials: 'same-origin',
        body: qs.stringify({
            [window.csrfTokenName]: window.csrfTokenValue,
            first_name: formData.get('fields[firstName]'),
            last_name: formData.get('fields[lastName]'),
            phone: formData.get('fields[phone]'),
            email: formData.get('fields[email]'),
            enquiry_type: 'smsf',
        }),
    });
}

async function saveForm(formData) {
    return fetch('/', {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
        body: formData,
    });
}

class SteppedForm {
    constructor(container) {
        if (!container) return;

        // Initialise selectors
        this.form = document.querySelector('.js-stepped-form');
        this.alert = document.querySelector('.js-stepped-form-alert');

        // Bind context to methods that need to be referenced
        this.handleWindowClose = this.handleWindowClose.bind(this);
        this.saveOnUnload = this.saveOnUnload.bind(this);

        // Initialise event listeners
        this.addEventListeners();
    }

    addEventListeners() {
        // Warn user about lost progress on window close
        window.addEventListener('beforeunload', this.handleWindowClose);
        // Use fancy navigator.sendBeacon to save on unload if browser supports it
        if (typeof navigator.sendBeacon === 'function') {
            window.addEventListener('unload', this.saveOnUnload);
        }

        // Attach form submission handler
        this.form.addEventListener('submit', (event) => {
            event.preventDefault();

            // Check last section
            if (this.validateSection(this.form)) {
                this.submit();
            }
        });

        focusGuard(this.form);
    }

    handleWindowClose(event) {
        // Fallback to async submit if browser doesn't support navigator.sendBeacon
        if (typeof navigator.sendBeacon === 'undefined') {
            this.submit();
        }
        /* eslint-disable no-param-reassign */
        event.returnValue = 'Are you sure you want to quit?';
        return 'Are you sure you want to quit?';
    }

    validateSection(section) {
        clearErrors();
        // Select all fields that are in the section. Iterate through them and add
        // `is-invalid` class to empty fields. The variable `isValid` will return false if there
        // are any invalid fields.
        const fields = [...section.querySelectorAll('.js-validate')];
        const isValid = fields.reduce((prev, field) => {
            const validationType = field.dataset.validate || '';

            // Field is a radio button
            if (validationType === 'radio') {
                const options = Array.from(field.querySelectorAll('input[type=radio]'));
                const complete = options.some(option => option.checked);

                if (complete) {
                    return prev && true;
                }
                options.forEach(option => option.parentNode.classList.add('is-invalid'));
                return prev && false;

            }

            // Field is a checkbox
            if (field.getAttribute('type') === 'checkbox' && !field.checked) {
                field.classList.add('is-invalid');
                return prev && false;
            }

            // Field is empty
            if (!field.value) {
                field.classList.add('is-invalid');
                return prev && false;
            }

            // Field has a validation pattern
            if (validationType) {
                return prev && this.validateField(field, validationType);
            }

            // Field is fine
            field.classList.remove('is-invalid');
            return prev && true;
        }, true);

        // If all fields are filled, push the current index state to the completed array, move to the
        // next slide and increment current index.
        if (isValid) {
            section.querySelector('.stepped-form__validation').classList.remove('is-visible');
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: 'smsfCreation',
                creationStep: 'Submit Step 1',
            });
            return true;
        }

        section.querySelector('.stepped-form__validation').classList.add('is-visible');
        return false;

    }

    validateField(field, type) {
        if (!(type in validations.patterns)) {
            // There is no validation pattern defined for this type
            return true;
        }

        if (!field.value.match(validations.patterns[type])) {
            // Field is invalid
            this.insertError(field, validations.messages[type]);
            return false;
        }

        // Field is valid
        return true;
    }

    /**
     * showAlert() Displays a message for a brief moment
     * @param {string} message The message to display
     */
    showAlert(message = 'An unexpected error occurred, please try again later.') {
        this.alert.textContent = message;
        this.alert.classList.add('is-visible');
        window.setTimeout(() => {
            this.alert.classList.remove('is-visible');
        }, 6000);
    }

    /**
     * handleErrors() Shows errors below their respective fields
     * @param {Object} errors An object representing the errors returned from server
     */
    handleErrors(errors) {
        forEach(errors, (value, key) => {
            const field = document.querySelector(`[data-name=${key}]`);
            this.insertError(field, value[0]);
        });
    }

    insertError(field, error) {
        field.classList.add('is-invalid');
        field.insertAdjacentHTML('afterend', `
            <div class="stepped-form__error">
                ${error}
            </div>
        `);
        this.slider.resize();
    }

    saveOnUnload() {
        navigator.sendBeacon('/', new FormData(this.form));
    }

    async submit() {
        const formData = new FormData(this.form);

        try {
            const [saveFormResponse, createUserResponse] = await Promise.all([
                saveForm(formData),
                createUser(formData),
            ]);

            const [formJson, userJson] = await Promise.all([
                saveFormResponse.json(),
                createUserResponse.json(),
            ]);

            if ('errors' in formJson) {
                this.showAlert('Some of your answers are invalid. Please correct them and try submitting again.');
                this.handleErrors(formJson.errors);
            } else {
                window.removeEventListener('beforeunload', this.handleWindowClose);
                window.removeEventListener('unload', this.saveOnUnload);

                const params = qs.stringify({
                    firstName: formData.get('fields[firstName]'),
                    lastName: formData.get('fields[lastName]'),
                    email: formData.get('fields[email]'),
                    phone: formData.get('fields[phone]'),
                    request: formData.get('fields[request]'),
                    uuid: ('user_id' in userJson) ? userJson.user_id : '',
                });

                const redirect = formData.get('smsfSubmitUrl') || 'https://uat.sixpark.com.au/assessment';
                window.location.href = `${redirect}?${params}`;
            }
        } catch (err) {
            window.bugsnagClient.notify(err);
            this.showAlert();
        }
    }
}

const container = document.querySelector('.js-stepped-form');
export default new SteppedForm(container);



// WEBPACK FOOTER //
// ./app/scripts/components/stepped-form.js